import { createSlice } from "@reduxjs/toolkit";
import {
  getAdminPrescriptions,
  getAdminPrescriptionById,
  acceptAdminPrescription,
  rejectAdminPrescription,
  acceptMultipleAdminPrescriptions,
  rejectMultipleAdminPrescriptions,
} from "../Thunk/prescriptionThunk";

const initialState = {
  documents: [],
  pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
  currentPatient: null,
  currentDocuments: [],
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
  successMessage: null,
};

const prescriptionSlice = createSlice({
  name: "prescriptions",
  initialState,
  reducers: {
    clearPrescriptionError: (state) => {
      state.error = null;
    },
    clearPrescriptionSuccess: (state) => {
      state.successMessage = null;
    },
    clearPrescriptionDetail: (state) => {
      state.currentPatient = null;
      state.currentDocuments = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminPrescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload?.documents || [];
        const p = action.payload?.pagination || {};
        const page = Math.max(1, Number(p.page) || 1);
        const limit = Math.max(1, Number(p.limit) || 50);
        const total = Math.max(0, Number(p.total) || 0);
        state.pagination = {
          page,
          limit,
          total,
          totalPages: Math.max(1, Math.ceil(total / limit)),
        };
      })
      .addCase(getAdminPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load prescriptions";
      })

      .addCase(getAdminPrescriptionById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getAdminPrescriptionById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentPatient = action.payload?.patient || null;
        state.currentDocuments = action.payload?.documents || [];
      })
      .addCase(getAdminPrescriptionById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || "Failed to load prescription details";
      })

      .addCase(acceptAdminPrescription.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(acceptAdminPrescription.fulfilled, (state) => {
        state.actionLoading = false;
        state.successMessage = "Prescription accepted successfully";
      })
      .addCase(acceptAdminPrescription.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to accept prescription";
      })

      .addCase(rejectAdminPrescription.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(rejectAdminPrescription.fulfilled, (state) => {
        state.actionLoading = false;
        state.successMessage = "Prescription rejected successfully";
      })
      .addCase(rejectAdminPrescription.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to reject prescription";
      })

      .addCase(acceptMultipleAdminPrescriptions.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(acceptMultipleAdminPrescriptions.fulfilled, (state) => {
        state.actionLoading = false;
        state.successMessage = "Selected prescriptions accepted successfully";
      })
      .addCase(acceptMultipleAdminPrescriptions.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to accept multiple prescriptions";
      })

      .addCase(rejectMultipleAdminPrescriptions.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(rejectMultipleAdminPrescriptions.fulfilled, (state) => {
        state.actionLoading = false;
        state.successMessage = "Selected prescriptions rejected successfully";
      })
      .addCase(rejectMultipleAdminPrescriptions.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Failed to reject multiple prescriptions";
      });
  },
});

export const {
  clearPrescriptionError,
  clearPrescriptionSuccess,
  clearPrescriptionDetail,
} = prescriptionSlice.actions;

export default prescriptionSlice.reducer;